import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import Button from 'core/elements/button/Button'
import Card from 'core/elements/card'
import Divider from 'core/elements/Divider'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { useCallback, useState } from 'react'
import { mapVMHAConditions, VMHADisplayStatus } from '../helpers'
import VmhaConditionsStatus from '../VmhaConditionsStatus'
import { vmhaClusterHostRequirementsDoc } from 'app/docLinks'
import LinkButton from 'core/components/buttons/LinkButton'
import { ClusterVmhaStatus } from 'api-client/model'

interface Props {
  isExpanded: boolean
}

const useStyles = makeStyles<Theme, Partial<Props>>((theme: Theme) => ({
  overview: {
    display: 'flex',
    gridGap: '24px',
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: '8px 0px',
    border: `1px solid ${theme.palette.grey[200]}`,
    width: 370, // To match that given on overview pages in case contents are greater
    height: ({ isExpanded }: { isExpanded: boolean }) => (isExpanded ? 'auto' : 96),
    '& .card-body': {
      padding: '0px 32px 16px 32px',
    },
  },
  customCardHeader: {
    display: 'flex',
    margin: '16px 32px 0px',
    gap: 8,
    alignItems: 'center',
    cursor: 'pointer',
  },
  iconContainer: {
    display: 'grid',
    gap: 8,
  },
  headerText: {
    color: theme.palette.grey[500],
    lineHeight: 'normal',
  },
  properties: {
    display: 'grid',
    gap: 24,
  },
  caret: {
    marginLeft: 'auto',
    cursor: 'pointer',
    fontSize: 14,
    color: theme.palette.grey[600],
  },
  button: {
    border: 'none',
  },
  divider: {
    background: theme.palette.grey[200],
    margin: '16px 0px',
  },
  linkButton: {
    width: '100%',
    marginTop: theme.spacing(2),
    border: 'none',
    alignContent: 'center',
    '& .button-text': {
      justifyContent: 'center',
    },
  },

  enableVmhaButton: {
    width: '100%',
    marginTop: theme.spacing(2),
    border: 'none',
    alignContent: 'center',
    '& .button-text': {
      justifyContent: 'center',
    },
  },
}))

export const clusterVmhaBadgeMap = {
  [ClusterVmhaStatus.Protected]: { label: VMHADisplayStatus.PROTECTED, variant: 'success' },
  [ClusterVmhaStatus.Degraded]: { label: VMHADisplayStatus.DEGRADED, variant: 'warning' },
  [ClusterVmhaStatus.Unprotected]: { label: VMHADisplayStatus.NOT_PROTECTED, variant: 'error' },
}

export default function ClusterHostGroupVmhaStatusDetails({
  vmhaClusterDetails,
  loading,
  onClickRefresh,
  clusterHostGroup,
  vmhaDisabled,
  onClickEnableVmha,
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const classes = useStyles({ isExpanded })

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  return (
    <Card
      className={classes.card}
      title={
        <div className={classes.customCardHeader} onClick={toggleExpand}>
          <div className={classes.iconContainer}>
            <Text variant="subtitle2">VM High Availability</Text>
            <BadgeWithIcon
              variant={
                !clusterHostGroup?.vmHighAvailability?.enabled
                  ? 'default'
                  : loading
                  ? 'default'
                  : clusterVmhaBadgeMap[vmhaClusterDetails?.vmha_status]?.variant
              }
              text={
                !clusterHostGroup?.vmHighAvailability?.enabled
                  ? 'Disabled'
                  : loading
                  ? 'Loading'
                  : clusterVmhaBadgeMap[vmhaClusterDetails?.vmha_status]?.label || 'Unknown'
              }
              data-testid="cluster-vmha-status-badge"
              icon={
                !clusterHostGroup?.vmHighAvailability?.enabled
                  ? 'circle-minus'
                  : loading
                  ? 'circle-notch fa-spin'
                  : vmhaClusterDetails?.vmha_status === ClusterVmhaStatus.Protected
                  ? 'check'
                  : 'xmark'
              }
              solid={!clusterHostGroup?.vmHighAvailability?.enabled ? false : true}
            />
          </div>
          <FontAwesomeIcon className={classes.caret} solid>
            {isExpanded ? 'chevron-up' : 'chevron-down'}
          </FontAwesomeIcon>
        </div>
      }
    >
      {/* <Text variant="h4">{item?.name}</Text> */}
      {isExpanded && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.overview}>
            <Text className={classes.headerText} variant="caption2">
              All conditions must be met for this service to run:
            </Text>
            <Button
              icon="refresh"
              variant="secondary"
              onClick={onClickRefresh}
              disabled={loading}
              className={classes.button}
            >
              Refresh
            </Button>
          </div>
          <div className={classes.properties}>
            <VmhaConditionsStatus
              title=""
              loading={loading}
              conditions={mapVMHAConditions(vmhaClusterDetails)}
            />
          </div>
          <LinkButton className={classes.linkButton} url={vmhaClusterHostRequirementsDoc}>
            Learn More
          </LinkButton>
          {!clusterHostGroup?.vmHighAvailability?.enabled && (
            <Button
              icon="power-off"
              variant="primary"
              onClick={onClickEnableVmha}
              disabled={vmhaDisabled || loading}
              className={classes.enableVmhaButton}
            >
              Enable VM HA
            </Button>
          )}
        </>
      )}
    </Card>
  )
}
